import { forwardRef, useState } from 'react';

import { Check } from '../../icons/lucide/Check';
import { Platform } from '../../platform';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { mixHex } from '../../utils/color';
import { useMenuHighlight } from '../DropdownMenu/MenuHighlightContext';
import { MarqueeLabel } from '../MarqueeLabel';
import { renderMenuControl } from './MenuListItem.controls';
import {
  ICON_SIZE_FOR,
  resolveMenuListItemIconColor,
  TRAILING_ICON_SIZE_FOR,
} from './MenuListItem.icons';
import type { MenuListItemProps } from './MenuListItem.types';
import {
  menuListItemAccentVariants,
  menuListItemDescriptionVariants,
  menuListItemTextVariants,
  menuListItemVariants,
} from './MenuListItem.variants';

const IS_WEB = Platform.OS === 'web';

// Detached ref shape — same rationale as Button.tsx / Pressable.tsx: pnpm
// hoists multiple @types/react copies so `Ref<T>` becomes nominally distinct
// across consumer apps even when versions match.
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * Cross-platform menu list item. Renders identically on web and React Native.
 *
 * Sibling to {@link Button}, NOT a Button variant — list rows have a flat
 * surface (no asymmetric bevel / drop shadow), full-width left-aligned
 * layout, and a persistent `selected` state that Button doesn't model.
 *
 * Mirrors Figma "Flashka Design System" file `YZK9r3wDhvw1pY60KlVEIj`,
 * frame `1000:8509` "Menu Item":
 *
 *   - XL (56px) — compact dropdown menu rows
 *   - L  (48px) — Mobile / iPad / web (touch-first)
 *   - M  (44px) — iPad + web
 *   - S  (32px) — Desktop dense menus
 *
 * Variants: `secondary` (neutral), `primary` (neutral at rest, brand purple
 * on hover/pressed/selected), `error` (red). See `MenuListItem.variants.ts`
 * for the full per-state colour table.
 *
 * @example
 * ```tsx
 * import { MenuListItem } from '@flashka/shared/ui';
 * import { Sparkles, ChevronRight } from '@flashka/shared/ui/icons';
 *
 * // Plain row
 * <MenuListItem onPress={open}>Settings</MenuListItem>
 *
 * // With leading icon + persistent selected state — auto-shows check
 * <MenuListItem
 *   variant="primary"
 *   leftIcon={Sparkles}
 *   selected={current === 'magic'}
 *   onPress={() => setCurrent('magic')}>
 *   Magic mode
 * </MenuListItem>
 *
 * // Submenu row — explicit trailing icon overrides auto-check
 * <MenuListItem leftIcon={Sparkles} rightIcon={ChevronRight} onPress={expand}>
 *   Effects
 * </MenuListItem>
 * ```
 */
function MenuListItemInner(
  {
    children,
    description,
    descriptionClassName,
    onPress,
    selected = false,
    disabled = false,
    fullWidth = true,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    showCheck = true,
    iconColor: iconColorOverride,
    accentColor,
    leftSlot,
    rightSlot,
    rightControl,
    checkable,
    variant,
    size,
    className,
    textClassName,
    accessibilityLabel,
    testID,
    as,
    webProps,
    nativeProps,
    onHoverIn,
    onHoverOut,
    // Slot-style consumers (Radix Tooltip/Popover `Trigger asChild`, etc.)
    // clone the row and attach extra props — pointer/focus handlers, aria-*
    // — at the top level. Funnel them through to the Pressable, which
    // composes them with its own handlers. Same pattern as Button / Chip.
    ...injected
  }: MenuListItemProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const { isDark } = useTheme();
  // True inside a `<MenuHighlight>` list — one shared indicator owns the hover
  // and focus wash, so this row must not paint its own.
  const slidingHighlight = useMenuHighlight();
  const resolvedVariant = variant ?? 'secondary';
  const resolvedSize = size ?? 'l';
  const isAccent = accentColor != null;
  const hasDescription = description != null && description !== '';

  // Radio / checkbox / toggle are all checkable (they drive `aria-checked`),
  // but only a ticked checkbox / selected radio flips the WHOLE row to its
  // `selected` variant (Figma 4036:30431) — a toggle communicates its state
  // itself (iOS-settings style), so its row stays neutral. `keybinding` is
  // display-only and never selects. Everything below reads `isSelected`.
  const controlChecked =
    rightControl && rightControl.type !== 'keybinding' ? rightControl.checked : undefined;
  const isSelected =
    selected || (controlChecked === true && rightControl?.type !== 'toggle');

  // A radio/checkbox/toggle control makes the row a checkable menu item;
  // otherwise a plain menu item. (`Select` rows deliberately stay control-free
  // — they draw their state via `selected` alone and announce it through
  // `checkable`, below.)
  const resolvedRole =
    rightControl?.type === 'radio'
      ? 'radio'
      : rightControl?.type === 'checkbox'
        ? 'checkbox'
        : rightControl?.type === 'toggle'
          ? 'switch'
          : 'menuitem';
  // `checkable` (no control): web swaps in the checkable menu-item role, which
  // the Pressable's role union cannot name, so it travels as `webProps.role`
  // and `accessibilityRole` steps aside for it; native keeps `menuitem`.
  const checkableAs = rightControl === undefined ? checkable : undefined;
  const webCheckableRole =
    IS_WEB && checkableAs !== undefined
      ? checkableAs === 'checkbox'
        ? 'menuitemcheckbox'
        : 'menuitemradio'
      : undefined;

  // Native-only interaction state for accent rows. On web, hover/press are
  // resolved by pure CSS (the `web:` accent classes read `--menu-accent`), so
  // these never update there — they're wired only when `!IS_WEB && isAccent`,
  // keeping web rows and every non-accent row free of per-interaction
  // re-renders (preserves the sidebar's zero-re-render-on-hover behaviour).
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const textClass = cn(
    menuListItemTextVariants({
      variant: resolvedVariant,
      size: resolvedSize,
      selected: isSelected,
      disabled,
    }),
    textClassName,
  );

  // Paint the variant text COLOUR on the Pressable container so SVG icons
  // can inherit it via CSS `currentColor` on web. Same trick Button.tsx uses
  // to keep icon tints in step with the row's text colour across light /
  // dark / selected states without re-resolving per icon.
  const containerClass = cn(
    menuListItemVariants({
      variant: resolvedVariant,
      size: resolvedSize,
      selected: isSelected,
      disabled,
      fullWidth,
      hasDescription,
      slidingHighlight,
    }),
    // Accent overlay (web only — `web:`-gated, `!`-important so it beats the
    // variant's bg/border). Concatenated ONLY when `accentColor` is set, so
    // non-accent rows are byte-identical. On native these classes are stripped
    // by NativeWind; the accent bg/border there comes from inline style below.
    isAccent && menuListItemAccentVariants({ selected: isSelected }),
    textClass,
    className,
  );

  // Icon colour precedence: explicit `iconColor` > `accentColor` > variant.
  // Same platform split as Button.tsx for the variant path: `currentColor` on
  // web (CSS inherits from the Pressable's text-* class), explicit hex on
  // native (react-native-svg doesn't honour `currentColor`).
  const iconColor =
    iconColorOverride ??
    (accentColor != null
      ? accentColor
      : IS_WEB
        ? 'currentColor'
        : resolveMenuListItemIconColor(resolvedVariant, isSelected, isDark));
  // Leading and trailing icons size independently per Figma's per-size bands
  // (e.g. XL = 26px leading, 22px trailing; S = 14px leading, 16px trailing).
  const leftIconSize = ICON_SIZE_FOR[resolvedSize];
  const trailingIconSize = TRAILING_ICON_SIZE_FOR[resolvedSize];

  // Trailing affordance resolution. Priority:
  //   1. `rightControl` — built-in keybinding / radio / checkbox (4036:30431).
  //   2. Explicit `rightSlot` — full escape hatch (badges, custom chips).
  //   3. Explicit `rightIcon` — overrides the auto-check.
  //   4. Auto `Check` when `isSelected && showCheck` (default behaviour).
  //   5. Nothing.
  //
  // Slot wins over icon for symmetry with `leftSlot` / `leftIcon`.
  let trailing: React.ReactNode = null;
  if (rightControl !== undefined) {
    trailing = renderMenuControl(rightControl, resolvedSize, resolvedVariant);
  } else if (rightSlot !== undefined) {
    trailing = rightSlot;
  } else if (RightIcon) {
    trailing = <RightIcon color={iconColor} size={trailingIconSize} />;
  } else if (isSelected && showCheck) {
    trailing = <Check color={iconColor} size={trailingIconSize} />;
  }

  // The label fills the available horizontal space so the trailing slot
  // hugs the right edge regardless of label length. `flex-1 text-left`
  // works on both NativeWind and web Tailwind.
  //
  // Two-line layout (a `description` is set): a `flex-1 min-w-0` column holds
  // the title above the description. `min-w-0` lets both lines shrink below
  // their content width so the ellipsis + marquee can engage (the marquee
  // measures the clamped width). Both lines run through the shared
  // `MarqueeLabel` — clamp with ellipsis, hover-scroll a truncated line on web,
  // ellipsis-only on native. A non-string title (custom JSX) is rendered as-is
  // above the description, with no clamp.
  // `flex-1` is `flex-basis: 0%`, so the label contributes NOTHING to an
  // auto-width row's intrinsic size. That is fine — and wanted — when the row
  // is `fullWidth` and simply fills its parent. But in a HUGGING container (a
  // `Dropdown` panel sizing itself to its widest row) it collapses the parent
  // to its minimum and ellipsizes every label. `grow` keeps `flex-basis: auto`,
  // so the text's own width propagates outward while it still fills a wider
  // row and still shrinks-with-ellipsis when space runs out.
  const labelFlexClass = fullWidth ? 'min-w-0 flex-1' : 'min-w-0 grow';

  let labelNode: React.ReactNode;
  if (hasDescription) {
    labelNode = (
      <View className={cn(labelFlexClass, 'flex-col items-start justify-center')}>
        {typeof children === 'string' ? (
          <MarqueeLabel label={children} className={textClass} />
        ) : (
          children
        )}
        <MarqueeLabel
          label={description}
          className={cn(
            menuListItemDescriptionVariants({ size: resolvedSize }),
            descriptionClassName,
          )}
        />
      </View>
    );
  } else if (typeof children === 'string') {
    // Same `MarqueeLabel` clamp as the two-line branch above. A plain `Text`
    // here could NOT truncate on native (no `numberOfLines`), so a label wider
    // than its row overflowed and was clipped mid-glyph by the panel — e.g. a
    // fixed-width anchored menu, or any longer localisation. `min-w-0` lets the
    // flex child shrink below its content so the clamp can engage at all.
    labelNode = (
      <MarqueeLabel label={children} className={cn(labelFlexClass, textClass)} />
    );
  } else {
    labelNode = children;
  }

  // ── Accent wiring ───────────────────────────────────────────────────────
  // Web: hand the raw colour to an inline `--menu-accent` CSS var; the
  // `menuListItemAccentVariants` classes turn it into bg/border via `color-mix`
  // (pure CSS hover/active — no re-render). Native: precompute the per-state
  // bg/border hex and apply via inline style (RN has no CSS vars / color-mix),
  // mirroring web's 10%/15% washes (mix toward white in light, black in dark).
  // Both merge onto — never clobber — caller-supplied web/nativeProps.style.
  let mergedWebProps =
    accentColor != null && IS_WEB
      ? {
          ...webProps,
          style: {
            ...(webProps?.style as React.CSSProperties | undefined),
            '--menu-accent': accentColor,
          } as React.CSSProperties,
        }
      : webProps;

  let mergedNativeProps = nativeProps;
  if (accentColor != null && !IS_WEB) {
    const active = pressed || isSelected;
    const showWash = active || hovered;
    const accentBg = showWash
      ? mixHex(accentColor, isDark ? 'black' : 'white', active ? 0.85 : 0.9)
      : 'transparent';
    mergedNativeProps = {
      ...nativeProps,
      style: [
        {
          backgroundColor: accentBg,
          borderColor: showWash ? accentColor : 'transparent',
        },
        (nativeProps as { style?: unknown } | undefined)?.style,
      ],
    };
  }

  // Announce the checkable state of a radio / checkbox control (a11y). Web maps
  // to `aria-checked`; native to `accessibilityState.checked`. `keybinding`
  // leaves `controlChecked` undefined, so plain rows are untouched.
  if (controlChecked !== undefined) {
    if (IS_WEB) {
      mergedWebProps = { ...mergedWebProps, 'aria-checked': controlChecked };
    } else {
      mergedNativeProps = {
        ...mergedNativeProps,
        accessibilityState: {
          ...(mergedNativeProps as { accessibilityState?: object } | undefined)
            ?.accessibilityState,
          checked: controlChecked,
        },
      };
    }
  } else if (checkableAs !== undefined) {
    // The selection IS the check state. Web: the checkable menu-item role +
    // `aria-checked`. Native: `checked` for a multi-pick row, `selected` for a
    // single-choice one (see the prop's doc for why the two differ).
    if (IS_WEB) {
      mergedWebProps = {
        ...mergedWebProps,
        role: webCheckableRole,
        'aria-checked': selected,
      };
    } else {
      mergedNativeProps = {
        ...mergedNativeProps,
        accessibilityState: {
          ...(mergedNativeProps as { accessibilityState?: object } | undefined)
            ?.accessibilityState,
          ...(checkableAs === 'checkbox' ? { checked: selected } : { selected }),
        },
      };
    }
  }

  // Track native hover/press for the accent wash while still forwarding the
  // consumer's handlers. No-op on web / non-accent (handlers pass through
  // unchanged, so the web path stays pure-CSS with zero extra re-renders).
  const trackNative = isAccent && !IS_WEB;
  const handleHoverIn = trackNative
    ? (e: PressEvent) => {
        setHovered(true);
        onHoverIn?.(e);
      }
    : onHoverIn;
  const handleHoverOut = trackNative
    ? (e: PressEvent) => {
        setHovered(false);
        onHoverOut?.(e);
      }
    : onHoverOut;
  const handlePressIn = trackNative ? () => setPressed(true) : undefined;
  const handlePressOut = trackNative ? () => setPressed(false) : undefined;

  return (
    <Pressable
      {...injected}
      ref={ref}
      as={as}
      className={containerClass}
      disabled={disabled}
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={webCheckableRole ? undefined : resolvedRole}
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}>
      {LeftIcon ? (
        <LeftIcon color={iconColor} size={leftIconSize} />
      ) : leftSlot !== undefined ? (
        leftSlot
      ) : null}
      {labelNode}
      {trailing}
    </Pressable>
  );
}

/**
 * Reasoning for the explicit `forwardRef` type-cast pattern: see Button.tsx.
 * pnpm's hoisted `@types/react` copies make `Ref<T>` nominally distinct
 * across consumers, so we keep the ref type as a structural `AnyRef<T>` and
 * narrow at the use-site.
 */
// The `as never` / `as unknown` hops mirror Chip.tsx exactly: the widened
// inner props (`& Record<string, unknown>` for slot-injected passthrough)
// don't structurally overlap the public signature.
export const MenuListItem = forwardRef(MenuListItemInner as never) as unknown as (
  props: MenuListItemProps & { ref?: AnyRef<HTMLElement> },
) => ReturnType<typeof MenuListItemInner>;

// Re-export PressEvent so consumers don't have to dive into primitives.
export type { PressEvent };
